import { ApiClient } from './apiClient';

export interface UserInfo {
  email: string | null;
  sub: string | null;
  username: string | null;
  email_verified: boolean | string;
  giacom_username: string | null;
  giacom_cost_centre_1: string | null;
  giacom_cost_centre_2: string | null;
  giacom_cost_centre_3: string | null;
}

export interface PhoneNumber {
  phoneNumber: string;
}

export interface UsageRecord {
  type: 'data' | 'call' | 'sms' | 'unknown';
  mobile_number: string;
  event_date: string;
  time: string;
  duration: number;
  volume: number;
  cost: number;
  country: string;
  network: string;
  file_name: string;
  imported_at: string;
}

export interface UsageByRangeRecord {
  period: string;
  total_call_duration: number;
  call_count: number;
  total_data_volume: number;
  data_count: number;
  sms_count: number;
}

export interface MobileService {
  id: number;
  cli: string;
  account_number: string;
  tariff: string | null;
  network: string;
  connection_date: string | null;
  renewal_date: string | null;
  active: number;
  last_seen: string;
  first_imported: string;
  status: string;
  username: string;
  sim_serial: string | null;
  service_id: string;
  start_date: string;
  end_date: string;
  contract_length_months: number;
  cost_centre_1: string;
  cost_centre_2: string | null;
  cost_centre_3: string | null;
  supplier: string | null;
  technology_type: string | null;
}

export interface MobileServicesResponse {
  username: MobileService[];
  cost_centre_1: MobileService[];
  cost_centre_2: MobileService[];
  cost_centre_3: MobileService[];
  cli: PhoneNumber[];
}

export class GiacomApiService {
  /**
   * Get authenticated user info from Cognito
   */
  static async getUserInfo(): Promise<UserInfo> {
    return ApiClient.get<UserInfo>('/giacom/info');
  }

  /**
   * Get phone numbers for the authenticated user
   */
  static async getPhoneNumbers(): Promise<PhoneNumber[]> {
    const response = await ApiClient.get<{ phoneNumbers: PhoneNumber[] }>('/giacom/phone-numbers');
    return response.phoneNumbers;
  }

  /**
   * Get usage records for user's phone numbers
   */
  static async getUsageRecords(): Promise<UsageRecord[]> {
    const response = await ApiClient.get<{ records: UsageRecord[] }>('/giacom/usage');
    return response.records;
  }

  /**
   * Get aggregated usage by date range
   * @param period - 'day', 'month', or 'year'
   * @param startDate - Optional start date (YYYY-MM-DD)
   * @param endDate - Optional end date (YYYY-MM-DD)
   */
  static async getUsageByRange(
    period: 'day' | 'month' | 'year' = 'month',
    startDate?: string,
    endDate?: string
  ): Promise<UsageByRangeRecord[]> {
    const params = new URLSearchParams({ period });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return ApiClient.get<UsageByRangeRecord[]>(`/giacom/usage-by-range?${params.toString()}`);
  }

  /**
   * Get mobile services data
   */
  static async getMobileServices(): Promise<MobileServicesResponse> {
    return ApiClient.get<MobileServicesResponse>('/giacom/mobile-services');
  }

  /**
   * Health check endpoint (public, no auth required)
   */
  static async healthCheck(): Promise<{
    status: string;
    service: string;
    timestamp: string;
  }> {
    return ApiClient.get('/giacom/health');
  }
}
